import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../services/apiClient';
import { isProxied, googleEnabled } from '../proxy-config';
import AgeGatePrompt, {
  AgeGateRefusal,
  ageAttestTokenOf,
  isAgeAttestationRequired,
  isAgeRefusal,
  markAgeGateBlocked,
  postAgeAttestation,
  storeAttestedSession,
} from '../components/AgeGate';

const AGE_ATTEST_FAILED = 'We could not verify your date of birth. Please try again.';
const AGE_SIGN_IN_AGAIN = 'Please sign in again to continue.';

// 999.1551: REACT_APP_AUTH_API_URL is the standard CRA build-time env-var
// pattern (pre-approved). The fallback is dev-only; production builds inject
// the real URL at build time. Already gated through proxy-config's isProxied
// check — when proxied (localdev/production), same-origin is used and this
// env var is not read at all.
const AUTH_API_URL = isProxied ? '' : (process.env.REACT_APP_AUTH_API_URL || 'http://localhost:5010');

// 999.17990: ProtectedRoute (App.js) sends unauthenticated users here with
// ?next=<pathname+search> so a page like /consent?oauth_state=X survives the
// login round-trip instead of always landing on /account. `next` is
// attacker-reachable (anyone can craft the URL), so it must stay a same-origin
// IN-APP path — reject protocol-relative ("//evil"), backslash-smuggled
// ("/\\evil", which the WHATWG URL parser treats as authority slashes for
// http(s)) and scheme URLs before ever handing it to navigate(). The
// startsWith('/') check alone rejects scheme URLs (they never start with a
// bare slash); the URL-parse re-check is defense in depth against any other
// parser trick, same technique ../utils/firstPartyRedirect.js uses for the
// absolute-URL case (TOTPVerifyPage/LogoutPage).
// Exported (not just local) so TOTPVerifyPage.jsx can apply the SAME
// same-origin-path check to `next` on its own fallback when no `redirect` is
// present (999.17990 round 2) — the shared util location the dispatcher
// suggested (utils/firstPartyRedirect.js) is ring-2 out of this lane's scope
// (tree-lock denial, see receipt); this file is already ring-0 in-scope and
// is `next`'s natural owner, so the function lives here once instead of
// being duplicated in TOTPVerifyPage.jsx.
export function safeInternalNext(next) {
  if (!next || typeof next !== 'string') return null;
  if (!next.startsWith('/') || next.startsWith('//') || next.startsWith('/\\')) return null;
  try {
    const resolved = new URL(next, window.location.origin);
    if (resolved.origin !== window.location.origin) return null;
  } catch (_) {
    return null;
  }
  return next;
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ssoChecking, setSsoChecking] = useState(false);
  const { login, oauthLogin, user, loadUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const redirectApp = searchParams.get('app');
  const redirectUrl = searchParams.get('redirect');
  const safeNext = safeInternalNext(searchParams.get('next'));
  // PKCE (RFC 7636): the calling app generates the verifier/challenge and passes the
  // challenge through here so the issued code is PKCE-bound (token.js AC5 usedPKCE branch).
  const codeChallenge = searchParams.get('code_challenge');
  const codeChallengeMethod = searchParams.get('code_challenge_method');
  const codeIssued = useRef(false);
  // 999.18844 — COPPA age gate. agePrompt: null | { kind: 'oauth', providerData }
  // (first social login: the server refused to create the account until a date
  // of birth is supplied) | { kind: 'attest', token } (#5: an existing account
  // that never attested — the login door answered 403 + a short-lived
  // age_attest token instead of a session; the token alone authorizes the POST).
  const [agePrompt, setAgePrompt] = useState(null);
  const [ageRefused, setAgeRefused] = useState(false);
  const [ageSubmitting, setAgeSubmitting] = useState(false);
  const [ageError, setAgeError] = useState('');

  // If user is already logged in, continue to the app / next page
  useEffect(() => {
    if (user) continueAfterLogin();
  }, [user]);

  // Silent SSO check: if we have a redirect but no user, check the SSO cookie
  useEffect(() => {
    if (!redirectUrl || user) return;

    let cancelled = false;

    async function checkSSO() {
      setSsoChecking(true);
      try {
        const res = await fetch(`${AUTH_API_URL}/api/auth/sso-check`, {
          credentials: 'include',
        });
        const data = await res.json();

        if (cancelled) return;

        if (data.authenticated && data.user) {
          // SSO session is valid — get tokens via a fresh login-free authorize flow
          // We need tokens first, so call the sso-token endpoint to get them
          const tokenRes = await fetch(`${AUTH_API_URL}/api/auth/sso-token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          });
          const tokenData = await tokenRes.json();

          if (cancelled) return;

          if (tokenRes.ok && tokenData.tokens) {
            apiClient.setTokens(tokenData.tokens.accessToken, tokenData.tokens.refreshToken);
            localStorage.setItem('auth_user', JSON.stringify(data.user));
            // The AuthContext will pick up the user on next render,
            // triggering the useEffect above to call issueCodeAndRedirect
            window.location.reload();
            return;
          }
          // 999.18844 #5 — the SSO account never attested: prompt, don't fail.
          const attestToken = ageAttestTokenOf({ status: tokenRes.status, data: tokenData });
          if (attestToken) {
            setAgePrompt({ kind: 'attest', token: attestToken });
            setSsoChecking(false);
            return;
          }
        }
      } catch {
        // SSO check failed — fall through to login form
      }

      if (!cancelled) {
        setSsoChecking(false);
      }
    }

    checkSSO();
    return () => { cancelled = true; };
  }, [redirectUrl, user]);

  // After successful login, generate an auth code and redirect back to the calling app
  async function issueCodeAndRedirect() {
    if (!redirectUrl || codeIssued.current) return;
    codeIssued.current = true;

    try {
      const data = await apiClient.post('/api/auth/authorize', {
        redirect_uri: redirectUrl,
        ...(codeChallenge ? { code_challenge: codeChallenge, code_challenge_method: codeChallengeMethod || 'S256' } : {}),
      });
      if (data.code) {
        const url = new URL(redirectUrl);
        url.searchParams.set('code', data.code);
        window.location.href = url.toString();
        return;
      }
      // No code returned — do NOT navigate to the unvalidated redirectUrl.
      // With nothing to deliver, redirecting is a pure open redirect (999.2084).
      // Show the login form; keep codeIssued=true so the still-set `user` can't
      // re-fire the [user] effect into an authorize loop.
      setSsoChecking(false);
    } catch (err) {
      if (err.status === 401) {
        // Stale session: bouncing back to the app just sends it straight
        // back here — the juggler<->auth infinite loop (999.2052). Purge the
        // dead session (clearTokens also drops the cached auth_user). loadUser
        // with no token nulls `user`, which both renders the form and prevents
        // any re-entry, so resetting codeIssued here is safe.
        apiClient.clearTokens();
        codeIssued.current = false;
        setSsoChecking(false);
        await loadUser();
        return;
      }
      // 999.2084 / 999.2053: any other failure — including a 400 for an
      // off-allowlist redirect_uri the backend now rejects — must NOT navigate
      // to the unvalidated redirectUrl (open redirect, phishing bounce, no code
      // delivered). Show the login form. Keep codeIssued=true and leave the
      // (valid) session intact: resetting it would let loadUser re-set `user`
      // and re-trigger the [user] effect into an authorize loop.
      setSsoChecking(false);
    }
  }

  async function continueAfterLogin() {
    if (redirectUrl) {
      await issueCodeAndRedirect();
    } else {
      navigate(safeNext || '/account');
    }
  }

  async function handleLoginSuccess() {
    await continueAfterLogin();
  }

  // 999.18844 #5 — trade the age_attest token + date of birth for the session
  // pair, store it, and reload the user: the [user] effect continues the login.
  // Under the floor the server locks the account; no session ever existed here.
  async function submitAgeAttestation(birthDate) {
    setAgeError('');
    setAgeSubmitting(true);
    try {
      const data = await postAgeAttestation(agePrompt.token, birthDate);
      storeAttestedSession(data);
      setAgePrompt(null);
      await loadUser();
    } catch (err) {
      if (isAgeRefusal(err)) {
        markAgeGateBlocked();
        setAgePrompt(null);
        setAgeRefused(true);
        return;
      }
      if (err && err.status === 401) {
        // spent or expired attest token: only a fresh login carries a new one
        setAgePrompt(null);
        setError(AGE_SIGN_IN_AGAIN);
        return;
      }
      setAgeError(AGE_ATTEST_FAILED);
    } finally {
      setAgeSubmitting(false);
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.requiresTOTP) {
        // 999.17990 round 2 (harrison): this branch dropped `next` entirely —
        // a 2FA user bounced from /consent?oauth_state=X always landed on
        // /account post-verify. Carry the already-validated safeNext through
        // the same way redirect/app are carried; TOTPVerifyPage re-validates
        // it with the SAME safeInternalNext check before using it.
        // 999.19315: the totp_pending token rides in router state, not the URL
        // (kept out of history/Referer); a reload drops it → sign in again.
        const totpQuery = new URLSearchParams();
        if (redirectUrl) totpQuery.set('redirect', redirectUrl);
        if (redirectApp) totpQuery.set('app', redirectApp);
        if (safeNext) totpQuery.set('next', safeNext);
        navigate(`/totp-verify?${totpQuery}`, { state: { totpPendingToken: result.totpPendingToken } });
        return;
      }

      await handleLoginSuccess(result);
    } catch (err) {
      const attestToken = ageAttestTokenOf(err);
      if (attestToken) {
        setAgePrompt({ kind: 'attest', token: attestToken });
        return;
      }
      setError(err.data?.message || err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  async function googleLogin(providerData) {
    setError('');
    setLoading(true);

    try {
      const result = await oauthLogin('google', providerData);
      setAgePrompt(null);
      await handleLoginSuccess(result);
    } catch (err) {
      // 999.18844 #5 — an EXISTING unattested account: 403 + age_attest token.
      const attestToken = ageAttestTokenOf(err);
      if (attestToken) {
        setAgePrompt({ kind: 'attest', token: attestToken });
        return;
      }
      // 999.18844 — a first social login must attest a date of birth before
      // the server creates the account; retry the SAME credential with it.
      if (isAgeAttestationRequired(err)) {
        setAgePrompt({ kind: 'oauth', providerData });
        return;
      }
      if (isAgeRefusal(err)) {
        markAgeGateBlocked();
        setAgePrompt(null);
        setAgeRefused(true);
        return;
      }
      setAgePrompt(null);
      setError(err.data?.message || err.message || 'Google login failed');
    } finally {
      setLoading(false);
    }
  }

  // 999.2083 — send the RAW Google ID token; the server verifies its
  // signature + audience (google-auth-library verifyIdToken) and derives the
  // identity. The client no longer decodes the token or asserts any identity
  // claim (email/sub/email_verified) — those were trusted blindly pre-fix.
  const handleGoogleSuccess = (credentialResponse) =>
    googleLogin({ credential: credentialResponse.credential });

  const handleAgeSubmit = (birthDate) => {
    if (agePrompt.kind === 'oauth') return googleLogin({ ...agePrompt.providerData, birthDate });
    return submitAgeAttestation(birthDate);
  };

  const handleGoogleError = () => {
    setError('Google sign-in failed. Please try again.');
  };

  // Show loading state while SSO check is in progress
  if (ssoChecking) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1>Signing in...</h1>
          <p className="subtitle">Checking your session</p>
        </div>
      </div>
    );
  }

  if (ageRefused || agePrompt) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1>{ageRefused ? 'Sign In' : 'One more step'}</h1>
          {ageRefused ? (
            <AgeGateRefusal />
          ) : (
            <AgeGatePrompt
              context={agePrompt.kind === 'oauth' ? 'registration' : 'existing'}
              onSubmit={handleAgeSubmit}
              submitting={loading || ageSubmitting}
              error={ageError}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1>Sign In</h1>
        <p className="subtitle">
          {redirectApp ? `Sign in to continue to ${redirectApp}` : 'Welcome back'}
        </p>

        {error && <div className="error-message" role="alert" aria-live="assertive">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            {/* B-006: explicit htmlFor links label to input */}
            <label htmlFor="login-email">Email</label>
            {/* B-005: removed autoFocus — it caused a focus trap on re-render loops */}
            <input id="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" />
          </div>
          <div className="form-group">
            <label htmlFor="login-password">Password</label>
            <input id="login-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="current-password" />
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {/* Gated on the SAME flag App.js uses to mount GoogleOAuthProvider
            (999.5069). GoogleLogin calls useGoogleOAuth, which THROWS
            "Google OAuth components must be used within GoogleOAuthProvider"
            when the provider is absent — a render-phase throw that unwinds the
            whole tree, so the entire login page went BLANK instead of just
            losing its Google button. Any build without
            REACT_APP_GOOGLE_CLIENT_ID (which infra/scripts/frontends-build.sh
            does not pass) hit this: no email field, no password field, nothing.
            Email/password sign-in below does not depend on Google and must keep
            working when Google is unconfigured. */}
        {googleEnabled && (
          <>
            <div className="divider"><span>or</span></div>

            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                theme="outline"
                size="large"
                text="signin_with"
                shape="rectangular"
              />
            </div>
          </>
        )}

        <div className="auth-links">
          <Link to={`/register${redirectUrl ? `?redirect=${encodeURIComponent(redirectUrl)}&app=${redirectApp || ''}` : ''}`}>
            Don't have an account? Sign up
          </Link>
        </div>
      </div>
    </div>
  );
}
